<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar usuario" : "Nuevo usuario" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card
          tag="article"
          no-body
          style="max-width: 760px"
          class="custom-card"
        >
          <b-card-header
            header-tag="section"
            class="custom-card-header d-flex align-items-center"
          >
            <h3>
              {{ editing ? "Datos del usuario" : "Datos del nuevo usuario" }}
            </h3>
          </b-card-header>
          <b-card-body class="custom-card-body p-0">
            <section class="form-section">
              <div>
                <h2 class="form-subtitle">Informacion general</h2>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="first_name">Nombres *</label>
                    <input
                      id="first_name"
                      type="text"
                      v-model="form.first_name"
                      autocomplete="off"
                      placeholder="Ingresar nombres"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error': $v.form.first_name.$error,
                      }"
                    />
                    <FormError
                      v-if="$v.form.first_name.$error && !$v.form.first_name.required"
                      message="Nombres son requeridos"
                    />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="last_name">Apellidos *</label>
                    <input
                      id="last_name"
                      type="text"
                      v-model="form.last_name"
                      autocomplete="off"
                      placeholder="Ingresar apellidos"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error': $v.form.last_name.$error,
                      }"
                    />
                    <FormError
                      v-if="$v.form.last_name.$error && !$v.form.last_name.required"
                      message="Apellidos son requeridos"
                    />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="email">Correo electronico *</label>
                    <input
                      id="email"
                      type="text"
                      v-model.trim="form.email"
                      autocomplete="off"
                      placeholder="Ingresar correo electronico"
                      class="custom-form-control"
                      :class="{
                        'custom-form-control-error': $v.form.email.$error,
                      }"
                    />
                    <FormError
                      v-if="$v.form.email.$error && !$v.form.email.required"
                      message="Correo electronico es requerido"
                    />
                    <FormError
                      v-if="$v.form.email.$error && !$v.form.email.email"
                      message="Ingresa un correo electronico valido"
                    />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="role">Perfil *</label>
                    <FormSelect
                      id="role"
                      :items="profileTypes"
                      v-model="form.role"
                      defaultOption="Elegir perfil"
                      :showError="$v.form.role.$error"
                      :disabled="editing"
                    />
                    <FormError
                      v-if="$v.form.role.$error && !$v.form.role.required"
                      message="Perfil es requerido"
                    />
                  </div>
                </div>
              </div>

              <!-- Afiliado (solo cuando role=AFFILIATE) -->
              <div class="row" v-if="form.role === 'AFFILIATE'">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="affiliate_id">Afiliado *</label>
                    <FormSelect
                      id="affiliate_id"
                      :items="affiliateOptions"
                      v-model="form.affiliate_id"
                      defaultOption="Seleccionar afiliado"
                      :showError="$v.form.affiliate_id && $v.form.affiliate_id.$error"
                      :disabled="editing"
                    />
                    <FormError
                      v-if="$v.form.affiliate_id && $v.form.affiliate_id.$error && !$v.form.affiliate_id.required"
                      message="Debe seleccionar un afiliado"
                    />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link
                      :to="{ name: 'config-user-list' }"
                      class="button button-light"
                    >
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError } from "wize-admin";

import { UsersService, AffiliateService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";
import { email, required, requiredIf } from "vuelidate/lib/validators";

export default {
  components: {
    FormError,
    FormSelect,
  },
  data() {
    return {
      profileTypes: [
        LocalConstants.ProfileTypes.ADMIN,
        LocalConstants.ProfileTypes.AFFILIATE,
      ],
      affiliates: [],
      form: {
        email: null,
        first_name: null,
        last_name: null,
        role: LocalConstants.ProfileTypes.ADMIN.key,
        affiliate_id: null,
      },
      editing: false,
    };
  },
  validations: {
    form: {
      email: { email, required },
      first_name: { required },
      last_name: { required },
      role: { required },
      affiliate_id: {
        required: requiredIf(function () {
          return this.form.role === "AFFILIATE";
        }),
      },
    },
  },
  computed: {
    affiliateOptions() {
      return this.affiliates.map((a) => ({
        key: a.id,
        label: a.name,
      }));
    },
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "form.role": function (val) {
      if (val !== "AFFILIATE") {
        this.form.affiliate_id = null;
      }
    },
  },
  methods: {
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          const el = document.querySelector(".form-error");
          if (el) {
            this.$smoothScroll({
              scrollTo: el,
              updateHistory: false,
              offset: -50,
            });
          }
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);

        const payload = {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
          role: this.form.role,
        };

        if (this.form.role === "AFFILIATE" && this.form.affiliate_id) {
          payload.affiliate_id = parseInt(this.form.affiliate_id);
        }

        if (!this.editing) {
          await UsersService.save(payload);
          this.$router.push({ name: "config-user-list" });
        } else {
          await UsersService.update(this.$route.params.id, payload);
          this.load(this.$route.params.id);
        }

        Alert.success("Se almaceno la informacion correctamente");
      } catch (ex) {
        console.error(ex);
        if (ex.response && ex.response.status === 409) {
          Alert.error("Este correo ya esta registrado por otro usuario");
        } else if (ex.errors && ex.errors.message) {
          const raw = ex.errors.message;
          const msg = raw.includes('|') ? raw.split('|').slice(1).join('|') : raw;
          Alert.error(msg);
        } else {
          Alert.error("Ocurrio un error al guardar");
        }
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await UsersService.get(id);
        const payload = response.payload;

        this.form = {
          email: payload.email,
          first_name: payload.first_name,
          last_name: payload.last_name,
          role: payload.role,
          affiliate_id: payload.affiliate_id || null,
        };

        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar usuario");
        this.$router.push({ name: "config-user-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async loadAffiliates() {
      try {
        const response = await AffiliateService.list({ status: 1 }, { page: 0, size: 200 });
        const payload = response.payload;
        this.affiliates = Array.isArray(payload) ? payload : (payload?.items || []);
      } catch (e) {
        console.error("Error cargando afiliados:", e);
        this.affiliates = [];
      }
    },
  },
  async created() {
    await this.loadAffiliates();
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
